import { FieldPolicy, Reference } from '@apollo/client';

import { ApolloClientHelpers, Chats } from '@anyx/common-graphql';


const chatsQueryPolicy: FieldPolicy<Omit<Chats, 'chats'> & { chats: Reference[] }> = {
  keyArgs: ['input', ['storeId', 'chatIds', 'chatTypeFilter', 'messagingChannel', 'tagFilter']],
  merge(existing, incoming) {
    const existingChats = existing?.chats || [];
    const incomingChats = incoming?.chats || [];

    const incomingChatId: Record<string, number> = incomingChats.reduce((prev, curr, index) => {
      return {
        ...prev,
        [curr.__ref]: index,
      };
    }, {});

    const filteredExisting = existingChats.filter(
      (chat) => incomingChatId[chat.__ref] === undefined
    );

    return {
      ...incoming,
      hasNext: existing?.hasNext === false ? false : incoming.hasNext,
      chats: [...incomingChats, ...filteredExisting],
    };
  },
};

const chatQueryPolicy: FieldPolicy = {
  keyArgs: ['input', ['chatId']],
  read(_, { args, toReference }) {
    return toReference({
      __typename: 'Chat',
      id: args?.['input']?.['chatId'],
    });
  },
};

const lineChatEventsQueryPolicy: FieldPolicy = {
  keyArgs: ['input', ['chatId', 'keyword']],
};

const instagramChatEventsQueryPolicy: FieldPolicy = {
  keyArgs: ['input', ['chatId', 'keyword']],
};

const messengerChatEventsQueryPolicy: FieldPolicy = {
  keyArgs: ['input', ['chatId', 'keyword']],
};

const whatsappChatEventsQueryPolicy: FieldPolicy = {
  keyArgs: ['input', ['chatId', 'keyword']],
};

const lazadaChatEventsQueryPolicy: FieldPolicy = {
  keyArgs: ['input', ['chatId', 'keyword']],
};

const ShopeeChatEventsV2QueryPolicy: FieldPolicy = {
  keyArgs: ['input', ['chatId', 'keyword']],
};

const chatCustomerDetailV2QueryPolicy: FieldPolicy = {
  keyArgs: ['input', ['chatId']],
};

const customerNotesV3QueryPolicy: FieldPolicy = {
  keyArgs: ['input', ['customerGroupId', 'keyword']],
  merge(existing = [], incoming = [], { args }) {
    if (args?.['input']?.['after']) {
      return [...existing, ...incoming];
    }
    return incoming;
  },
};


const customerActivitiesQueryPolicyV4: FieldPolicy = {
  keyArgs: ['input', ['customerGroupId', 'channelCustomerFilter']],
  // combine incoming records, but maintain the total count from the initial fetch
  merge(existing = { records: [] }, incoming = { records: [] }, { args }) {
    if (args?.['input']?.['olderThan']) {
      return {
        ...incoming,
        total: existing?.total || incoming?.total,
        records: [...existing.records, ...incoming.records],
      };
    }

    return {
      ...incoming,
    };
  },
};

const customerOrdersV2QueryPolicy: FieldPolicy = {
  keyArgs: ['input', ['customerId', 'saleChannel']],
};

const anychatStoreQueryPolicy: FieldPolicy = {
  keyArgs: ['input', ['id']],
  merge: true,
};

const anyChatSaleChannelProductsQueryPolicy: FieldPolicy = {
  keyArgs: ['input', ['storeId', 'filter']],
  merge(existing = [], incoming = [], { args }) {
    const pageNumber = args?.['input']?.['pageNumber'] ?? 1;

    if (pageNumber > 1) {
      return [...existing, ...incoming];
    }

    return incoming;
  },
};

const lazadaVouchersQueryPolicy: FieldPolicy = {
  keyArgs: ['input', ['storeId', 'name', 'status', 'voucherType']],
  merge(existing, incoming, { args }) {
    const page = args?.['input']?.['page'] ?? 1;

    if (page > 1 && existing) {
      return {
        ...incoming,
        vouchers: [...(existing.vouchers || []), ...(incoming.vouchers || [])],
      };
    }

    return incoming;
  },
};

export const ChatFunctionApolloQueryPolicies: ApolloClientHelpers.QueryFieldPolicy = {
  Chats: chatsQueryPolicy,
  Chat: chatQueryPolicy,
  LineChatEvents: lineChatEventsQueryPolicy,
  InstagramChatEventsV2: instagramChatEventsQueryPolicy,
  MessengerChatEventsV2: messengerChatEventsQueryPolicy,
  WhatsAppChatEventsV2: whatsappChatEventsQueryPolicy,
  LazadaChatEventsV2: lazadaChatEventsQueryPolicy,
  ShopeeChatEventsV2: ShopeeChatEventsV2QueryPolicy,
  ChatCustomerDetailV2: chatCustomerDetailV2QueryPolicy,
  CustomerNotesV3: customerNotesV3QueryPolicy,
  CustomerActivitiesV4: customerActivitiesQueryPolicyV4,
  CustomerOrdersV2: customerOrdersV2QueryPolicy,
  AnyChatStore: anychatStoreQueryPolicy,
  AnyChatSaleChannelProducts: anyChatSaleChannelProductsQueryPolicy,
  LazadaVouchers: lazadaVouchersQueryPolicy,
};
